import {
  Badge,
  Button,
  Icon,
  Notification,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { CardContent } from '@hero/atoms';
import { AddTagModal } from '@hero/components/modals';
import { DetailItem } from '@hero/components/molecules';
import { ContactsEditModal } from '@hero/components/organisms/ContactsEditModal';
import { AUTOMATIONS_TAB, CONTACT_TYPE } from '@hero/constants';
import { useGetContacts, useGetTagList } from '@hero/services/contact-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { automationRemoveTagSchema } from '@hero/validations';

import * as S from './style';

function AutomationDetailRecipientsAccordion({ automation, onUpdate, setActiveTab }) {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [isEditRecipientModalOpen, setIsEditRecipientModalOpen] = useState(false);
  const [isEditTagModalOpen, setIsEditTagModalOpen] = useState(false);

  const contactType = automation?.contacts?.tags?.length ? CONTACT_TYPE.tags : CONTACT_TYPE.list;

  const { data: tagList } = useGetTagList(
    {
      filters: {
        ids: automation?.contacts?.tags
      },
      options: {
        includeContacts: true
      }
    },
    {
      enabled: automation?.contacts?.tags?.length > 0
    }
  );

  const { data: contacts } = useGetContacts(
    {
      filters: {
        tags: automation?.contacts?.tags
      }
    },
    {
      enabled: !!automation && automation?.contacts?.tags?.length > 0
    }
  );

  const totalContactCount = contacts?.docs?.length || 0;

  const onRemoveTag = tagId => {
    showNotification({
      title: t('areYouSureRemoveTag'),
      message: t('areYouSureRemoveTagMessage'),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      formData: [
        {
          name: 'confirm',
          title: (
            <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
              {t('removeTagModalConfirmLabel')}
            </Typography>
          ),
          type: 'text',
          maxLength: 10
        }
      ],
      formValidationSchema: automationRemoveTagSchema,
      secondaryButtonProps: { children: t('cancel') },
      primaryButtonProps: {
        color: 'error',
        children: t('yesRemove'),
        onClick: async () => {
          await onUpdate({
            params: { code: automation.code },
            body: {
              contacts: {
                tags: tagList?.docs?.filter(item => item.id !== tagId)?.map(item => item.id)
              }
            }
          });
        }
      }
    });
  };

  const addingMethods = {
    [CONTACT_TYPE.tags]: {
      addButton: (
        <Button variant="link" onClick={() => setIsEditTagModalOpen(true)}>
          <Icon name="add" color="var(--color-accent-primary-default)" />
          {t('addTags')}
        </Button>
      ),
      badgeLine: (
        <DetailItem title={`${t('tags')}: `} titleStyle={{ width: 100 }} className="item-d-flex">
          <S.BadgeWrapper>
            {tagList?.docs?.map(tag => (
              <Badge
                key={tag.id}
                variant="contained"
                color="bgWeak"
                rounded
                text={`${tag.name} (${tag.contactsCount})`}
                closable
                onClose={() => onRemoveTag(tag.id)}
              />
            ))}
          </S.BadgeWrapper>
        </DetailItem>
      ),
      badge: !automation?.contacts?.tags?.length ? (
        <Trans
          i18nKey="badgeIncludeAtLeastOneTagToActivate"
          values={{
            featureType: t('automation').toLocaleLowerCase(),
            contactType: t('tag').toLocaleLowerCase()
          }}
          components={{ b: <b /> }}
        />
      ) : (
        ''
      )
    },
    [CONTACT_TYPE.list]: {
      addButton: (
        <Button variant="link" onClick={() => setIsEditRecipientModalOpen(true)}>
          <Icon name="add" color="var(--color-accent-primary-default)" />
          {t('addRecipients')}
        </Button>
      ),
      badge: !automation?.contacts?.ids?.length ? (
        <Trans
          i18nKey="badgeIncludeAtLeastOneTagToActivate"
          values={{
            featureType: t('automation').toLocaleLowerCase(),
            contactType: t('recipient').toLocaleLowerCase()
          }}
          components={{ b: <b /> }}
        />
      ) : (
        ''
      )
    }
  };

  return (
    <>
      <CardContent>
        {addingMethods[contactType].badge && (
          <Notification
            status="warning"
            style={{ marginBottom: 24 }}
            message={<Typography variant="body2">{addingMethods[contactType].badge}</Typography>}
          />
        )}
        <S.RecipientHeader>
          <Button
            startIcon={<Icon name="preview" />}
            onClick={() => window.open(automation?.link?.preview, '_blank')}
            target="_blank"
            variant="text"
          >
            {t('viewGiftPreview')}
          </Button>
          <VerticalDivider $height={16} />
          <Button
            startIcon={<Icon name="contacts" />}
            onClick={() => setActiveTab(AUTOMATIONS_TAB.recipients)}
            variant="text"
          >
            {t('seeRecipientList')}
          </Button>
        </S.RecipientHeader>
        <S.RecipientsContent>
          <S.Col>
            {addingMethods[contactType].badgeLine}
            <DetailItem
              title={`${t('recipients')}: `}
              titleStyle={{ width: 100 }}
              className="item-d-flex"
            >
              {automation?.contacts?.ids?.length || totalContactCount}
            </DetailItem>
          </S.Col>
          {addingMethods[contactType].addButton}
        </S.RecipientsContent>
        {isEditRecipientModalOpen && (
          <ContactsEditModal
            isModalOpen={isEditRecipientModalOpen}
            isLiveFeature
            featureType="automation"
            onRequestClose={() => setIsEditRecipientModalOpen(false)}
            selectedContactIds={automation?.contacts?.ids || []}
            disabledColumns={[automation?.type === 'birthday' ? 'anniversary' : 'birthday']}
            updateContacts={async data => {
              await onUpdate({
                params: { code: automation?.code },
                body: {
                  contacts: {
                    ids: data
                  }
                }
              });
            }}
          />
        )}
        {isEditTagModalOpen && (
          <AddTagModal
            openModal={isEditTagModalOpen}
            onRequestClose={() => setIsEditTagModalOpen(false)}
            ids={automation?.contacts?.tags}
            onUpdate={async selectedRows => {
              const tags = Object.keys(selectedRows);
              await onUpdate({
                params: { code: automation?.code },
                body: {
                  contacts: {
                    tags
                  }
                }
              });
              setIsEditTagModalOpen(false);
            }}
          />
        )}
      </CardContent>
    </>
  );
}

export { AutomationDetailRecipientsAccordion };
